import {
  DataTable,
  DataTableColDef,
} from "@/lib/Components/DataTable/DataTable";
import {
  RelocationLineListItem,
  relocationLineListQuery,
  RelocationLineListVariables,
} from "@/app/Relocations/GraphQL/relocationLineReferenceListQuery";
import type { TableId } from "@/app/Common/Utils/table-id";

import { LockIcon, UnlockIcon } from "lucide-react";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { markRelocationLineAsSoldMutation } from "@/app/Relocations/GraphQL/relocationLineReferenceMutations";
import { TimeSinceNow } from "@/lib/Components/Common/TimeSinceNow";
import { Link } from "@/components/catalyst/link";
import { ModelLinkCell } from "@/lib/Components/DataTable/Cells/ModelLinkCell";
import { cn } from "@/lib/utils";
import { formatDate } from "@/lib/Formatters/formatDate";
import { VehicleIcon } from "@/app/Vehicles/Components/VehicleIcon";
import { inclusionIconMap } from "@/app/Relocations/Utils/inclusionIconMap";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
} from "@/components/catalyst/dropdown";
import { Button } from "@/components/catalyst/button";
import { Competitor } from "@/gql/graphql";

function formatCompetitor(competitor: Competitor | null | undefined): string {
  if (!competitor) return "";

  switch (competitor) {
    case Competitor.Transfercar:
      return "TransferCar";
    case Competitor.Coseats:
      return "CoSeats";
    case Competitor.Drivenow:
      return "DriveNow";
    case Competitor.Supplier:
      return "Withdrawn";
    default:
      return "";
  }
}

type RelocationLineColumnId =
  | "line"
  | "actions"
  | "relocation"
  | "booking"
  | "expires"
  | "sold_at";

export function RelocationLineTable({
  id,
  queryVariables,
}: {
  id: TableId;
  queryVariables?: Partial<RelocationLineListVariables>;
}) {
  const { mutateAsync, isPending } = useGqlMutation(
    markRelocationLineAsSoldMutation,
  );

  const columns: DataTableColDef<
    RelocationLineListItem,
    unknown,
    RelocationLineColumnId
  >[] = [
    {
      id: "line",
      header: "Line",
      cell: ({ row }) => {
        const reference = row.original;
        let color = "bg-green-500";

        if (reference.sold_at) {
          if (reference.booking) {
            color = "bg-yellow-500";
          } else {
            color = "bg-red-500";
          }
        }

        const competitor = formatCompetitor(row.original.sold_by);

        return (
          <div className="flex items-center gap-2">
            <span>{row.original.reference}</span>
            <span className={cn(color, "inline-flex h-2 w-2 rounded-full")} />
            {competitor ? (
              <span className="text-xs text-gray-500">({competitor})</span>
            ) : null}
          </div>
        );
      },
    },
    {
      id: "relocation",
      header: "Relocation",
      cell: ({ row }) => {
        return (
          <div>
            <ul>
              {row.original.group.relocations.map((r) => {
                return (
                  <li key={r.id}>
                    <Relocation relocation={r} />
                  </li>
                );
              })}
            </ul>
          </div>
        );
      },
    },
    {
      id: "booking",
      header: "Booking",
      cell: ({ row }) =>
        row.original.booking ? (
          <ModelLinkCell
            to="/$tenant/$orgId/supplier/{-$supplierId}/bookings/$bookingId"
            params={{
              bookingId: row.original.booking.id,
            }}
            viewTransition
          >
            {row.original.booking?.reference}
          </ModelLinkCell>
        ) : null,
    },
    {
      id: "sold_at",
      header: "Sold",
      accessorFn: (data) => data.sold_at,
      cell: ({ row }) => (
        <TimeSinceNow className="text-gray-500" time={row.original.sold_at} />
      ),
    },
    {
      id: "actions",
      cell: ({ row }) => {
        if (row.original.sold_at) {
          return (
            <Button
              plain
              isLoading={isPending}
              LeadingIcon={UnlockIcon}
              onClick={() => {
                return mutateAsync({
                  id: row.original.id,
                  sold: false,
                });
              }}
              className="text-red-500 hover:text-red-700"
              title="Release"
            />
          );
        }

        return (
          <Dropdown>
            <DropdownButton as={Button} plain isLoading={isPending}>
              {isPending ? null : <LockIcon className="h-5 w-5" />}
            </DropdownButton>
            <DropdownMenu>
              <DropdownItem
                onClick={() => {
                  return mutateAsync({
                    id: row.original.id,
                    sold: true,
                  });
                }}
              >
                Sold (No Competitor)
              </DropdownItem>
              <DropdownItem
                onClick={() => {
                  return mutateAsync({
                    id: row.original.id,
                    sold: true,
                    soldBy: Competitor.Transfercar,
                  });
                }}
              >
                Sold by TransferCar
              </DropdownItem>
              <DropdownItem
                onClick={() => {
                  return mutateAsync({
                    id: row.original.id,
                    sold: true,
                    soldBy: Competitor.Coseats,
                  });
                }}
              >
                Sold by CoSeats
              </DropdownItem>
              <DropdownItem
                onClick={() => {
                  return mutateAsync({
                    id: row.original.id,
                    sold: true,
                    soldBy: Competitor.Drivenow,
                  });
                }}
              >
                Sold by DriveNow
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <DataTable
      id={id}
      searchable={false}
      document={relocationLineListQuery}
      accessor={(a) => a.relocationLines}
      columns={columns}
      getQueryVariables={({ pagination }) => {
        return {
          first: pagination.pageSize,
          page: pagination.pageIndex,
          orderByReference: true,
          ...queryVariables,
        };
      }}
    />
  );
}

function Relocation({
  relocation,
}: {
  relocation: RelocationLineListItem["group"]["relocations"][number];
}) {
  return (
    <div className="flex items-center space-x-2">
      <VehicleIcon model={relocation.vehicle} />
      <div className="w-[200px] overflow-hidden">
        <p>
          <Link
            title={relocation.id}
            className="truncate text-blue-500 underline"
            from="/$tenant/$orgId/supplier/{-$supplierId}"
            to={
              "/$tenant/$orgId/supplier/{-$supplierId}/relocations/$relocationId"
            }
            params={(prev) => ({
              ...prev,
              relocationId: relocation.id,
            })}
          >
            {relocation.departureOffice.name} to{" "}
            {relocation.deliveryOffice.name}
            {` (${relocation.id})`}
          </Link>
        </p>
        <p className="text-xs text-gray-500">{relocation.vehicle.name}</p>
        <p className="text-xs text-gray-500">
          {`${formatDate(relocation.available_from_date)} - ${formatDate(
            relocation.available_to_date,
          )}`}
        </p>
        <ul>
          {relocation.inclusions.map(({ id, type, value, description }) => {
            const Icon = inclusionIconMap[type];

            return (
              <li
                key={id}
                className="flex items-center space-x-2 text-xs text-gray-500"
              >
                <Icon className="h-4 w-4 shrink-0 text-yellow-400" />
                {value ? (
                  <span>{formatCurrency(value, relocation.currency)}</span>
                ) : null}
                <div
                  className="line-clamp-1 truncate whitespace-pre-line"
                  dangerouslySetInnerHTML={{
                    __html: description ?? "--",
                  }}
                />
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
